import { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, ShieldOff, Lock, AlertCircle, Copy, Eye, EyeOff, KeyRound,
  Sparkles, Smartphone, Check, X, Activity, ScanLine,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { usePromoAuth } from "../context/AuthContext";

/* ════════════════════════════════════════════════════════════════════════
   SECURITY & 2FA PAGE — premium redesign
   ════════════════════════════════════════════════════════════════════════ */
export default function Security() {
  const { refresh } = usePromoAuth();
  const [status, setStatus] = useState({ enabled: false, backup_remaining: 0 });
  const [setupData, setSetupData] = useState(null);
  const [showCodes, setShowCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [totp, setTotp] = useState("");

  const loadStatus = async () => {
    try { const { data } = await api.get("/aff-panel/auth/2fa/status"); setStatus(data); } catch (_) { /* ignore */ }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/aff-panel/auth/2fa/setup");
      setSetupData(data);
    } catch { toast.error("Failed to start setup"); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    if (!pwdConfirm) return toast.error("Enter your password");
    if (!/^\d{6}$/.test(totp.trim())) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/aff-panel/auth/2fa/enable", {
        password: pwdConfirm, secret: setupData.secret, totp_code: totp.trim(),
      });
      toast.success("2FA enabled!");
      setShowCodes(data.backup_codes || []);
      setSetupData(null);
      setTotp(""); setPwdConfirm("");
      await loadStatus(); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to enable 2FA"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!pwdConfirm) return toast.error("Enter your password");
    if (!/^\d{6}$/.test(totp.trim())) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/aff-panel/auth/2fa/disable", { password: pwdConfirm, totp_code: totp.trim() });
      toast.success("2FA disabled");
      setPwdConfirm(""); setTotp("");
      await loadStatus(); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to disable"); }
    finally { setBusy(false); }
  };

  const submitChange = async (e) => {
    e.preventDefault();
    if (!pwd.current || !pwd.next || !pwd.confirm) return toast.error("All fields required");
    if (pwd.next.length < 8) return toast.error("Min 8 characters");
    if (pwd.next !== pwd.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/aff-panel/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password updated");
      setPwd({ current: "", next: "", confirm: "" });
      setShowChange(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text).then(() => toast.success("Copied")); };

  /* security score — simple composite (2FA = +60, has password change = +40 baseline) */
  const score = useMemo(() => {
    let s = 40; // baseline (has password)
    if (status.enabled) s += 60;
    return Math.min(100, s);
  }, [status.enabled]);

  const scoreLevel = score >= 100 ? "Excellent"
    : score >= 70 ? "Strong"
    : score >= 50 ? "Good"
    : "At Risk";
  const scoreColor = score >= 100 ? "#10b981"
    : score >= 70 ? "#22c073"
    : score >= 50 ? "#facc15"
    : "#ef4444";

  return (
    <div className="pb-6">
      <PageHeader
        title="Security & 2FA"
        eyebrow="Account safety"
        subtitle="Premium accounts deserve premium protection."
      />

      {/* ═══════ SECURITY SCORE HERO ═══════ */}
      <div className="sec-hero p-fade-up">
        <div className="sec-hero-grid" aria-hidden />
        <div className="sec-hero-glow" aria-hidden style={{ background: `radial-gradient(420px 220px at 30% 30%, ${scoreColor}33, transparent 65%)` }} />
        <div className="sec-hero-body">
          {/* Score ring */}
          <div className="sec-score-wrap">
            <svg className="sec-score-ring" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
              <circle
                cx="60" cy="60" r="52" fill="none"
                stroke={scoreColor} strokeWidth="9" strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 326.7} 326.7`}
                strokeDashoffset="0"
                transform="rotate(-90 60 60)"
                style={{ transition: "stroke-dasharray 600ms cubic-bezier(.2,.7,.2,1), stroke 250ms" }}
              />
            </svg>
            <div className="sec-score-center">
              <div className="sec-score-num" style={{ color: scoreColor }}>{score}</div>
              <div className="sec-score-tag">/ 100</div>
            </div>
          </div>

          <div className="sec-hero-right">
            <div className="sec-hero-eyebrow" style={{ color: scoreColor }}>{scoreLevel}</div>
            <div className="sec-hero-title">Your Security Score</div>
            <div className="sec-hero-sub">
              {status.enabled
                ? "Two-factor is on — your account is well protected."
                : "Enable Two-Factor Authentication to dramatically increase security."}
            </div>
            <div className="sec-hero-stats">
              <div className="sec-mini-stat">
                <div className="sec-mini-icon" style={{ background: status.enabled ? "rgba(34,192,115,0.18)" : "rgba(239,68,68,0.16)", color: status.enabled ? "#22c073" : "#fca5a5", borderColor: status.enabled ? "rgba(34,192,115,0.4)" : "rgba(239,68,68,0.4)" }}>
                  {status.enabled ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                </div>
                <div>
                  <div className="sec-mini-val">{status.enabled ? "Active" : "Disabled"}</div>
                  <div className="sec-mini-lbl">2-Factor Auth</div>
                </div>
              </div>
              <div className="sec-mini-stat">
                <div className="sec-mini-icon" style={{ background: "rgba(212,175,55,0.16)", color: "#fde68a", borderColor: "rgba(212,175,55,0.4)" }}>
                  <KeyRound size={14} />
                </div>
                <div>
                  <div className="sec-mini-val">{status.backup_remaining ?? 0}</div>
                  <div className="sec-mini-lbl">Backup codes</div>
                </div>
              </div>
              <div className="sec-mini-stat">
                <div className="sec-mini-icon" style={{ background: "rgba(96,165,250,0.14)", color: "#93c5fd", borderColor: "rgba(96,165,250,0.4)" }}>
                  <Activity size={14} />
                </div>
                <div>
                  <div className="sec-mini-val">TLS 1.3</div>
                  <div className="sec-mini-lbl">Session</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════ TWO-FACTOR CARD ═══════ */}
      <div className="sec-card p-fade-up p-fade-up-1">
        <div className="sec-card-head">
          <div className="sec-card-icon" data-active={status.enabled ? "1" : "0"}>
            {status.enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
          </div>
          <div className="sec-card-titles">
            <div className="sec-card-row">
              <div className="sec-card-title">Two-Factor Authentication</div>
              {status.enabled
                ? <span className="sec-chip sec-chip-emerald"><ShieldCheck size={10} /> Enabled</span>
                : <span className="sec-chip sec-chip-amber"><AlertCircle size={10} /> Recommended</span>}
            </div>
            <div className="sec-card-sub">
              Use Google Authenticator, Authy, 1Password or any TOTP app to generate one-time codes.
            </div>
            {status.enabled && (
              <div className="sec-card-meta">
                Backup codes remaining: <b style={{ color: "#6ee7b7" }}>{status.backup_remaining}</b>
              </div>
            )}
          </div>
        </div>

        {/* Backup codes reveal */}
        {showCodes && (
          <div className="sec-backup-codes p-fade-up">
            <div className="sec-backup-head">
              <AlertCircle size={16} style={{ color: "#fde68a" }} />
              <div>
                <div className="sec-backup-title">Save these backup codes</div>
                <div className="sec-backup-sub">Each code can be used once if you lose access to your authenticator. They will not be shown again.</div>
              </div>
            </div>
            <div className="sec-backup-grid">
              {showCodes.map((c) => (
                <div key={c} className="sec-backup-tile">
                  <span>{c}</span>
                  <button onClick={() => copy(c)} aria-label="Copy code"><Copy size={12} /></button>
                </div>
              ))}
            </div>
            <div className="sec-backup-actions">
              <button onClick={() => copy(showCodes.join("\n"))} className="sec-link">Copy all</button>
              <button onClick={() => setShowCodes(null)} className="sec-link sec-link-dim">I&apos;ve saved them, hide</button>
            </div>
          </div>
        )}

        {/* CTA when disabled */}
        {!status.enabled && !setupData && (
          <button onClick={startSetup} disabled={busy} className="sec-btn sec-btn-primary sec-cta" data-testid="setup-2fa-btn">
            <Smartphone size={14} /> {busy ? "Loading…" : "Enable Two-Factor"}
          </button>
        )}

        {/* Setup wizard (when enabling) */}
        {!status.enabled && setupData && (
          <div className="sec-setup p-fade-up">
            {/* Step 1 — Scan */}
            <div className="sec-step">
              <div className="sec-step-num">1</div>
              <div className="sec-step-body">
                <div className="sec-step-title">Scan with your authenticator app</div>
                <div className="sec-step-sub">Open Google Authenticator, Authy, or 1Password and scan this QR code.</div>
                <div className="sec-qr-row">
                  <div className="sec-qr-wrap">
                    <img src={setupData.qr_data_url} alt="2FA QR code" />
                  </div>
                  <div className="sec-qr-side">
                    <div className="sec-qr-or">Or enter the secret manually</div>
                    <div className="sec-secret-row">
                      <span className="sec-secret">{setupData.secret}</span>
                      <button onClick={() => copy(setupData.secret)} className="sec-icon-btn" aria-label="Copy secret"><Copy size={13} /></button>
                    </div>
                    <div className="sec-qr-hint"><ScanLine size={11} /> Tip: hold your phone steady close to the screen.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 — Verify */}
            <div className="sec-step">
              <div className="sec-step-num">2</div>
              <div className="sec-step-body">
                <div className="sec-step-title">Confirm password and 6-digit code</div>
                <div className="sec-step-sub">Enter your account password, then the live 6-digit code from your app.</div>
                <div className="sec-confirm-grid">
                  <div className="sec-input-shell">
                    <Lock size={14} className="sec-input-ico" />
                    <input type="password" placeholder="Password" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} data-testid="2fa-password" />
                  </div>
                  <div className="sec-input-shell sec-input-totp">
                    <ShieldCheck size={14} className="sec-input-ico" />
                    <input
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000" maxLength={6} inputMode="numeric"
                      data-testid="2fa-totp"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="sec-action-row">
              <button onClick={() => { setSetupData(null); setTotp(""); setPwdConfirm(""); }} className="sec-btn sec-btn-ghost">Cancel</button>
              <button onClick={enable} disabled={busy} className="sec-btn sec-btn-primary" data-testid="2fa-enable-btn">
                <Sparkles size={14} /> {busy ? "Enabling…" : "Enable & finish"}
              </button>
            </div>
          </div>
        )}

        {/* Disable form (when 2FA on) */}
        {status.enabled && (
          <div className="sec-disable-block p-fade-up">
            <div className="sec-disable-warn">
              <AlertCircle size={14} /> Disabling 2FA reduces your account security. Re-verify identity to continue.
            </div>
            <div className="sec-confirm-grid">
              <div className="sec-input-shell">
                <Lock size={14} className="sec-input-ico" />
                <input type="password" placeholder="Password" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} />
              </div>
              <div className="sec-input-shell sec-input-totp">
                <ShieldCheck size={14} className="sec-input-ico" />
                <input
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000" maxLength={6} inputMode="numeric"
                />
              </div>
            </div>
            <button onClick={disable} disabled={busy} className="sec-btn sec-btn-danger" data-testid="2fa-disable-btn">
              <ShieldOff size={14} /> {busy ? "Disabling…" : "Disable 2FA"}
            </button>
          </div>
        )}
      </div>

      {/* ═══════ CHANGE PASSWORD CARD ═══════ */}
      <ChangePasswordCard
        pwd={pwd}
        setPwd={setPwd}
        show={show}
        setShow={setShow}
        busy={busy}
        showChange={showChange}
        setShowChange={setShowChange}
        onSubmit={submitChange}
      />

      <style>{styles}</style>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CHANGE PASSWORD CARD (inline, on Security page)
   ════════════════════════════════════════════════════════════════════════ */
function ChangePasswordCard({ pwd, setPwd, show, setShow, busy, showChange, setShowChange, onSubmit }) {
  const checks = useMemo(() => ({
    length: pwd.next.length >= 8,
    upper:  /[A-Z]/.test(pwd.next),
    digit:  /[0-9]/.test(pwd.next),
    symbol: /[^A-Za-z0-9]/.test(pwd.next),
    match:  pwd.next.length > 0 && pwd.next === pwd.confirm,
  }), [pwd.next, pwd.confirm]);

  const score = (checks.length ? 1 : 0) + (checks.upper ? 1 : 0) + (checks.digit ? 1 : 0) + (checks.symbol ? 1 : 0);
  const strengthLabel = ["Too weak", "Weak", "Okay", "Strong", "Excellent"][score];
  const strengthColor = ["#ef4444", "#f59e0b", "#facc15", "#22c073", "#10b981"][score];

  return (
    <div className="sec-card p-fade-up p-fade-up-2">
      <div className="sec-card-head">
        <div className="sec-card-icon" data-tone="key">
          <KeyRound size={20} />
        </div>
        <div className="sec-card-titles">
          <div className="sec-card-row">
            <div className="sec-card-title">Change Password</div>
            <span className="sec-chip sec-chip-jade"><Lock size={10} /> Encrypted</span>
          </div>
          <div className="sec-card-sub">Rotate your password every 60–90 days for the strongest protection.</div>
        </div>
        {!showChange && (
          <button onClick={() => setShowChange(true)} className="sec-btn sec-btn-ghost sec-card-cta" data-testid="open-change-pwd">
            <KeyRound size={13} /> Change…
          </button>
        )}
      </div>

      {showChange && (
        <form onSubmit={onSubmit} className="sec-pwd-form p-fade-up" data-testid="sec-change-pwd-form">
          {/* Fields */}
          <div className="sec-pwd-grid">
            <PwdField
              label="Current"
              icon={<Lock size={13} className="sec-input-ico" />}
              type={show ? "text" : "password"}
              value={pwd.current}
              onChange={(v) => setPwd({ ...pwd, current: v })}
              testid="pwd-current"
            />
            <PwdField
              label="New"
              icon={<KeyRound size={13} className="sec-input-ico" />}
              type={show ? "text" : "password"}
              value={pwd.next}
              onChange={(v) => setPwd({ ...pwd, next: v })}
              testid="pwd-new"
            />
            <PwdField
              label="Confirm"
              icon={<ShieldCheck size={13} className="sec-input-ico" />}
              type={show ? "text" : "password"}
              value={pwd.confirm}
              onChange={(v) => setPwd({ ...pwd, confirm: v })}
              testid="pwd-confirm"
            />
          </div>

          {/* Strength meter — only when user is typing new password */}
          {pwd.next && (
            <div className="sec-strength p-fade-up">
              <div className="sec-strength-bars">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="sec-strength-bar"
                    style={{ background: i < score ? strengthColor : "rgba(255,255,255,0.07)" }} />
                ))}
              </div>
              <div className="sec-strength-row">
                <span className="sec-strength-lbl" style={{ color: strengthColor }}>{strengthLabel}</span>
                <span className="sec-strength-hint">{score < 4 ? "Mix upper, digits & symbols for higher strength." : "Looks great!"}</span>
              </div>

              <div className="sec-checklist">
                <RuleChip ok={checks.length}  label="8+ characters" />
                <RuleChip ok={checks.upper}   label="Uppercase A-Z" />
                <RuleChip ok={checks.digit}   label="Number 0-9" />
                <RuleChip ok={checks.symbol}  label="Symbol e.g. !@#" />
                <RuleChip ok={checks.match}   label="Passwords match" />
              </div>
            </div>
          )}

          <div className="sec-action-row">
            <button type="button" onClick={() => setShow(!show)} className="sec-btn sec-btn-ghost sec-show-toggle">
              {show ? <EyeOff size={13} /> : <Eye size={13} />} {show ? "Hide" : "Show"}
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={() => { setShowChange(false); setPwd({ current: "", next: "", confirm: "" }); }} className="sec-btn sec-btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="sec-btn sec-btn-primary" data-testid="sec-change-pwd-submit">
              <Sparkles size={14} /> {busy ? "Updating…" : "Update Password"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────── */
function PwdField({ label, icon, type, value, onChange, testid }) {
  return (
    <div className="sec-pwd-field">
      <label className="sec-pwd-label">{label}</label>
      <div className="sec-input-shell">
        {icon}
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoComplete="off" />
      </div>
    </div>
  );
}

function RuleChip({ ok, label }) {
  return (
    <span className={`sec-rule ${ok ? "is-ok" : ""}`}>
      {ok ? <Check size={11} /> : <X size={11} />} {label}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   STYLES  (component-scoped)
   ════════════════════════════════════════════════════════════════════════ */
const styles = `
/* ─── Hero ─────────────────────────────────────────────────────────── */
.sec-hero {
  position: relative;
  border-radius: 24px;
  margin-bottom: 16px;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid rgba(34, 192, 115, 0.35);
  background:
    radial-gradient(900px 360px at -10% -20%, rgba(34, 192, 115, 0.18), transparent 60%),
    linear-gradient(180deg, rgba(10, 22, 16, 0.92), rgba(6, 8, 12, 0.98));
  box-shadow: 0 24px 70px -20px rgba(0,0,0,0.7);
}
.sec-hero-grid {
  position: absolute; inset: 0; pointer-events: none; opacity: 0.045; z-index: 0;
  background-image: radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px);
  background-size: 26px 26px;
  mask-image: radial-gradient(circle at 50% 30%, #000 0%, transparent 80%);
}
.sec-hero-glow { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
.sec-hero-body {
  position: relative; z-index: 1;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  padding: 22px 20px;
}
@media (min-width: 720px) {
  .sec-hero-body { grid-template-columns: 160px 1fr; gap: 28px; padding: 30px 32px; align-items: center; }
}

.sec-score-wrap { position: relative; width: 140px; height: 140px; margin: 0 auto; }
@media (min-width: 720px) { .sec-score-wrap { width: 160px; height: 160px; margin: 0; } }
.sec-score-ring { width: 100%; height: 100%; filter: drop-shadow(0 6px 22px rgba(34, 192, 115, 0.18)); }
.sec-score-center {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
}
.sec-score-num { font-size: 42px; font-weight: 900; line-height: 1; }
.sec-score-tag { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 2px; letter-spacing: 0.15em; font-weight: 700; }

.sec-hero-right { text-align: center; }
@media (min-width: 720px) { .sec-hero-right { text-align: left; } }
.sec-hero-eyebrow { font-size: 11px; font-weight: 800; letter-spacing: 0.22em; text-transform: uppercase; margin-bottom: 4px; }
.sec-hero-title { font-size: 22px; font-weight: 900; color: #fff; letter-spacing: -0.01em; }
.sec-hero-sub { font-size: 12.5px; color: rgba(255,255,255,0.55); margin-top: 6px; line-height: 1.5; max-width: 480px; }
.sec-hero-stats {
  margin-top: 16px;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
}
@media (min-width: 720px) { .sec-hero-stats { grid-template-columns: repeat(3, max-content); gap: 14px; } }
.sec-mini-stat {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
}
.sec-mini-icon {
  width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid;
}
.sec-mini-val { font-size: 13px; font-weight: 800; color: #fff; line-height: 1; }
.sec-mini-lbl { font-size: 9.5px; color: rgba(255,255,255,0.45); margin-top: 3px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 700; }

/* ─── Generic card ─────────────────────────────────────────────────── */
.sec-card {
  position: relative;
  margin-bottom: 14px;
  padding: 20px 22px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01));
  border: 1px solid rgba(255,255,255,0.07);
  backdrop-filter: blur(14px);
}
.sec-card-head { display: flex; gap: 14px; align-items: flex-start; }
.sec-card-icon {
  width: 46px; height: 46px; border-radius: 13px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, rgba(239,68,68,0.18), rgba(239,68,68,0.02));
  border: 1px solid rgba(239,68,68,0.35);
  color: #fca5a5;
}
.sec-card-icon[data-active="1"] {
  background: linear-gradient(135deg, rgba(34,192,115,0.22), rgba(34,192,115,0.02));
  border-color: rgba(34,192,115,0.5);
  color: #6ee7b7;
}
.sec-card-icon[data-tone="key"] {
  background: linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.02));
  border-color: rgba(212,175,55,0.4);
  color: #fde68a;
}
.sec-card-titles { flex: 1; min-width: 0; }
.sec-card-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sec-card-title { font-size: 16px; font-weight: 800; color: #fff; }
.sec-card-sub { font-size: 12.5px; color: rgba(255,255,255,0.55); margin-top: 4px; line-height: 1.5; }
.sec-card-meta { font-size: 11.5px; color: rgba(255,255,255,0.55); margin-top: 4px; }
.sec-card-cta { white-space: nowrap; flex-shrink: 0; align-self: flex-start; }

/* ─── Chips ────────────────────────────────────────────────────────── */
.sec-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  border: 1px solid;
}
.sec-chip-emerald { background: rgba(34,192,115,0.16); border-color: rgba(34,192,115,0.5); color: #6ee7b7; }
.sec-chip-amber   { background: rgba(245,158,11,0.13); border-color: rgba(245,158,11,0.4); color: #fcd34d; }
.sec-chip-jade    { background: rgba(167,243,208,0.10); border-color: rgba(167,243,208,0.3); color: #a7f3d0; }

/* ─── Buttons ──────────────────────────────────────────────────────── */
.sec-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  height: 42px; padding: 0 18px;
  border-radius: 12px;
  font-size: 12.5px; font-weight: 800;
  cursor: pointer; transition: all 0.18s ease;
  border: 1px solid transparent;
  white-space: nowrap;
  user-select: none;
}
.sec-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.sec-btn-primary {
  background: linear-gradient(135deg, #22c073 0%, #0ea05a 100%);
  color: #04130b;
  box-shadow: 0 8px 22px -8px rgba(34, 192, 115, 0.55), 0 0 0 1px rgba(34, 192, 115, 0.5) inset;
}
.sec-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px); filter: brightness(1.06);
}
.sec-btn-ghost {
  background: rgba(255,255,255,0.04);
  color: #f5f1e6;
  border-color: rgba(255,255,255,0.12);
  height: 38px; padding: 0 14px;
}
.sec-btn-ghost:hover:not(:disabled) {
  background: rgba(34,192,115,0.10);
  border-color: rgba(34,192,115,0.4);
  color: #d1fae5;
}
.sec-btn-danger {
  background: linear-gradient(135deg, rgba(239,68,68,0.22), rgba(239,68,68,0.04));
  color: #fca5a5;
  border-color: rgba(239,68,68,0.5);
}
.sec-btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(239,68,68,0.32), rgba(239,68,68,0.08));
  color: #fff;
}
.sec-cta { margin-top: 16px; }

/* ─── Setup steps ──────────────────────────────────────────────────── */
.sec-setup { margin-top: 18px; display: flex; flex-direction: column; gap: 18px; }
.sec-step { display: flex; gap: 14px; }
.sec-step-num {
  width: 28px; height: 28px; border-radius: 999px;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #22c073, #0ea05a);
  color: #04130b; font-weight: 900; font-size: 13px;
  box-shadow: 0 4px 12px -2px rgba(34, 192, 115, 0.5);
}
.sec-step-body { flex: 1; min-width: 0; }
.sec-step-title { font-size: 14px; font-weight: 800; color: #fff; }
.sec-step-sub { font-size: 11.5px; color: rgba(255,255,255,0.55); margin-top: 3px; line-height: 1.5; }

.sec-qr-row {
  display: flex; flex-direction: column; gap: 14px;
  margin-top: 12px;
}
@media (min-width: 540px) { .sec-qr-row { flex-direction: row; align-items: flex-start; } }
.sec-qr-wrap {
  width: 168px; height: 168px;
  padding: 10px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 8px 26px -8px rgba(0,0,0,0.5);
  flex-shrink: 0;
  align-self: center;
}
.sec-qr-wrap img { width: 100%; height: 100%; display: block; }
.sec-qr-side { flex: 1; min-width: 0; }
.sec-qr-or { font-size: 11px; color: rgba(255,255,255,0.5); margin-bottom: 6px; letter-spacing: 0.04em; }
.sec-secret-row {
  display: flex; align-items: center; gap: 6px;
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(34, 192, 115, 0.28);
  border-radius: 10px;
  padding: 8px 10px;
}
.sec-secret {
  flex: 1; min-width: 0;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  color: #d1fae5;
  word-break: break-all;
  letter-spacing: 0.02em;
}
.sec-qr-hint {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10.5px; color: rgba(255,255,255,0.4);
  margin-top: 10px;
}

.sec-confirm-grid {
  display: grid; grid-template-columns: 1fr; gap: 8px;
  margin-top: 10px;
}
@media (min-width: 540px) { .sec-confirm-grid { grid-template-columns: 1fr 150px; } }

/* ─── Input shell ──────────────────────────────────────────────────── */
.sec-input-shell {
  display: flex; align-items: center; gap: 8px;
  height: 44px; padding: 0 12px;
  background: rgba(8,6,14,0.7);
  border: 1px solid rgba(34, 192, 115, 0.25);
  border-radius: 12px;
  transition: all 0.18s;
}
.sec-input-shell:focus-within { border-color: #22c073; box-shadow: 0 0 0 4px rgba(34,192,115,0.18); }
.sec-input-shell input {
  flex: 1; min-width: 0;
  background: transparent; outline: none; border: none;
  color: #f5f1e6; font-size: 13px; font-weight: 600;
}
.sec-input-ico { color: rgba(167,243,208,0.6); flex-shrink: 0; }
.sec-input-totp input {
  font-family: ui-monospace, Menlo, monospace;
  text-align: center; letter-spacing: 0.34em; font-weight: 800; font-size: 16px;
}

.sec-action-row {
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
  margin-top: 18px;
}

/* ─── Disable block ────────────────────────────────────────────────── */
.sec-disable-block { margin-top: 14px; }
.sec-disable-warn {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  background: linear-gradient(90deg, rgba(239,68,68,0.10), rgba(239,68,68,0.02));
  border: 1px solid rgba(239,68,68,0.35);
  color: #fecaca;
  font-size: 12px;
}
.sec-disable-block .sec-btn { margin-top: 14px; }

/* ─── Backup codes ─────────────────────────────────────────────────── */
.sec-backup-codes {
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(212,175,55,0.10), rgba(212,175,55,0.02));
  border: 1px solid rgba(212,175,55,0.4);
}
.sec-backup-head { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 10px; }
.sec-backup-title { font-size: 13px; font-weight: 800; color: #fde68a; }
.sec-backup-sub { font-size: 11.5px; color: rgba(255,255,255,0.55); margin-top: 3px; }
.sec-backup-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
}
@media (min-width: 540px) { .sec-backup-grid { grid-template-columns: repeat(3, 1fr); } }
.sec-backup-tile {
  display: flex; align-items: center; justify-content: space-between; gap: 6px;
  padding: 8px 10px;
  background: rgba(0,0,0,0.35);
  border: 1px solid rgba(212,175,55,0.25);
  border-radius: 10px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12.5px;
  color: #fde68a;
}
.sec-backup-tile button {
  background: transparent; border: none; color: rgba(255,255,255,0.4); cursor: pointer;
  padding: 4px;
}
.sec-backup-tile button:hover { color: #fde68a; }
.sec-backup-actions { display: flex; gap: 12px; margin-top: 12px; }
.sec-link {
  background: transparent; border: none; cursor: pointer;
  color: #6ee7b7; font-size: 11.5px; font-weight: 700; text-decoration: underline;
  padding: 0;
}
.sec-link-dim { color: rgba(255,255,255,0.5); }

/* ─── Change password form ─────────────────────────────────────────── */
.sec-pwd-form { margin-top: 16px; }
.sec-pwd-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
@media (min-width: 720px) { .sec-pwd-grid { grid-template-columns: 1fr 1fr 1fr; } }
.sec-pwd-field {}
.sec-pwd-label { font-size: 10px; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; color: rgba(167,243,208,0.7); margin-bottom: 5px; display: block; }

.sec-strength { margin-top: 12px; padding: 12px 14px; border-radius: 12px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); }
.sec-strength-bars { display: flex; gap: 5px; margin-bottom: 8px; }
.sec-strength-bar { flex: 1; height: 5px; border-radius: 999px; transition: background 250ms ease; }
.sec-strength-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.sec-strength-lbl { font-size: 12px; font-weight: 800; }
.sec-strength-hint { font-size: 11px; color: rgba(255,255,255,0.4); }
.sec-checklist { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.sec-rule {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 9px; border-radius: 999px;
  font-size: 10.5px; font-weight: 700;
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.55);
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 200ms ease;
}
.sec-rule.is-ok {
  background: rgba(34,192,115,0.12);
  border-color: rgba(34,192,115,0.4);
  color: #6ee7b7;
}
.sec-show-toggle { padding: 0 12px !important; }
`;
